import { useState, useEffect, useMemo } from 'react';
import PeriodFilter, { usePeriodFilter } from '../components/PeriodFilter';
import { EMPTY_PERIOD } from '../components/common/ClearFiltersButton';
import MultiSelectFilter, { listValue, listParam } from '../components/common/MultiSelectFilter';
import FilterChips from '../components/common/FilterChips';
import SavedViews from '../components/common/SavedViews';
import EntityLink from '../components/EntityLink';
import api from '../utils/api';
import { localDateStr } from '../utils/dates';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import Pagination from '../components/Pagination';
import { useUrlParams } from '../hooks/useUrlState';
import SortTh, { useSort } from '../components/common/SortableHeader';
import { useToast } from '../context/FeedbackContext';
import PaymentsMadeTab from '../components/PaymentsMadeTab';

// Status filter options. Not mutually exclusive (a row can be both
// partially paid and waiting on partial AR), so each is its own test.
const STATUS_FILTERS = {
  unpaid: { label: 'Unpaid', test: r => !(r.ap_paid > 0.01) },
  partially_paid: { label: 'Partially paid', test: r => r.ap_paid > 0.01 },
  partial_ar: { label: 'Partial AR', test: r => r.ar_verified_paid < r.ar_amount - 0.01 },
  hours_pending: { label: 'Hours pending AR approval', test: r => r.hours_pending > 0.01 },
};

// Invoice type filter (2026-09-29). Passthrough = allowance / reimbursement
// / expense invoices, paid on a dollar cap rather than hours.
const INVOICE_TYPES = {
  standard: { label: 'Standard (hours)', test: r => (r.invoice_type || 'standard') === 'standard' },
  passthrough: { label: 'Allowance / Reimbursement', test: r => (r.invoice_type || 'standard') !== 'standard' },
};

// First Monday-Friday on/after a date - the PNC upload's default payment
// date (the server also rejects Federal Reserve holidays).
function nextWeekday(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  while (dt.getDay() === 0 || dt.getDay() === 6) dt.setDate(dt.getDate() + 1);
  return localDateStr(dt);
}

function distinctOptions(rows, idKey, nameKey) {
  const seen = new Map();
  for (const r of rows || []) {
    if (r[idKey] != null && !seen.has(r[idKey])) seen.set(r[idKey], r[nameKey] || '—');
  }
  return [...seen].map(([id, name]) => ({ id: String(id), name })).sort((a, b) => a.name.localeCompare(b.name));
}

export default function APPaymentsPage() {
  const toast = useToast();
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState(false);
  const { period, setPeriod, filterByPeriod } = usePeriodFilter("period_start");
  const [payForm, setPayForm] = useState(null); // { ap_invoice_id, invoice_number, amount_to_pay, vendor_name, contractor_name }
  const [saving, setSaving] = useState(false);
  const [payError, setPayError] = useState('');
  // Payment Date defaults to today - most payments are recorded the same
  // day they're wired, so this saves a click on the common case; still
  // freely editable when a payment actually happened on a different day.
  const todayStr = localDateStr();
  const EMPTY_FORM = { payment_date: todayStr, cash_amount: '', epd_rate: '', epd_amount: '', bgc_amount: '', advance_deduction_amount: '', trace_id: '', notes: '' };
  const [formData, setFormData] = useState(EMPTY_FORM);
  const { get, set } = useUrlParams();
  const search = get('search', '');
  // Multi-select: OR within a field, AND across fields (same as AP Invoices).
  const vendorFilter = listValue(get('vendor', ''));
  const contractorFilter = listValue(get('contractor', ''));
  const statusFilter = listValue(get('status', '')).filter(k => STATUS_FILTERS[k]);
  const typeFilter = listValue(get('type', '')).filter(k => INVOICE_TYPES[k]);
  const page = get('page', 1);
  const pageSize = get('pageSize', 25);
  const { sortKey, sortDir, handleSort, sortData } = useSort('contractor_name', 'asc');
  const [generatingAdvice, setGeneratingAdvice] = useState(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { message, errors }
  const [refreshing, setRefreshing] = useState(false);
  // PNC PINACLE upload file (2026-10-19): rows ticked in the table, the
  // export panel, and its server preview (accounts masked to last 4).
  const [selected, setSelected] = useState(() => new Set());
  const [pncOpen, setPncOpen] = useState(false);
  const [pncSource, setPncSource] = useState('selected'); // 'selected' | 'csv'
  const [pncDate, setPncDate] = useState(() => nextWeekday(todayStr));
  const [pncPreview, setPncPreview] = useState(null);
  const [pncBusy, setPncBusy] = useState(false);
  const [pncError, setPncError] = useState('');

  function load() {
    setError(false);
    api.get('/ap-invoices/payments-queue').then(r => setQueue(r.data)).catch(() => setError(true));
  }

  useEffect(() => { load(); }, []);

  // Dropdown options come from the queue itself, so they only ever list
  // vendors/contractors that actually have something to pay right now.
  const vendorOptions = useMemo(() => distinctOptions(queue, 'vendor_id', 'vendor_name'), [queue]);
  const contractorOptions = useMemo(() => distinctOptions(queue, 'contractor_id', 'contractor_name'), [queue]);

  const filtered = useMemo(() => {
    if (!queue) return [];
    const result = queue.filter(row => {
      if (vendorFilter.length && !vendorFilter.includes(String(row.vendor_id))) return false;
      if (contractorFilter.length && !contractorFilter.includes(String(row.contractor_id))) return false;
      if (statusFilter.length && !statusFilter.some(k => STATUS_FILTERS[k].test(row))) return false;
      if (typeFilter.length && !typeFilter.some(k => INVOICE_TYPES[k].test(row))) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (row.contractor_name || '').toLowerCase().includes(s) ||
        (row.vendor_name || '').toLowerCase().includes(s) ||
        (row.invoice_number || '').toLowerCase().includes(s) ||
        (row.ar_invoice_number || '').toLowerCase().includes(s);
    });
    return filterByPeriod(result);
  }, [queue, search, period, vendorFilter.join(), contractorFilter.join(), statusFilter.join(), typeFilter.join()]);

  const filtersActive = filtered.length !== (queue?.length || 0);

  const nameOf = (opts, id) => (opts.find(o => o.id === id) || {}).name || id;
  const chips = [
    ...vendorFilter.map(v => ({ key: `vendor:${v}`, label: `Vendor: ${nameOf(vendorOptions, v)}`, onRemove: () => set({ vendor: listParam(vendorFilter.filter(x => x !== v)), page: 1 }) })),
    ...contractorFilter.map(v => ({ key: `contractor:${v}`, label: `Contractor: ${nameOf(contractorOptions, v)}`, onRemove: () => set({ contractor: listParam(contractorFilter.filter(x => x !== v)), page: 1 }) })),
    ...statusFilter.map(v => ({ key: `status:${v}`, label: `Status: ${STATUS_FILTERS[v].label}`, onRemove: () => set({ status: listParam(statusFilter.filter(x => x !== v)), page: 1 }) })),
    ...typeFilter.map(v => ({ key: `type:${v}`, label: `Type: ${INVOICE_TYPES[v].label}`, onRemove: () => set({ type: listParam(typeFilter.filter(x => x !== v)), page: 1 }) })),
    ...(search ? [{ key: 'search', label: `Search: "${search}"`, onRemove: () => set({ search: '', page: 1 }) }] : []),
    ...(period.from || period.to ? [{ key: 'period', label: `Period: ${period.from || '…'} → ${period.to || '…'}`, onRemove: () => setPeriod(EMPTY_PERIOD) }] : []),
  ];
  const clearAllFilters = () => { set({ vendor: '', contractor: '', status: '', type: '', search: '', page: 1 }); setPeriod(EMPTY_PERIOD); };
  const savedViewState = { vendor: vendorFilter, contractor: contractorFilter, status: statusFilter, type: typeFilter, search, period };
  const applySavedView = v => {
    set({ vendor: listParam(v.vendor || []), contractor: listParam(v.contractor || []), status: listParam(v.status || []), type: listParam(v.type || []), search: v.search || '', page: 1 });
    setPeriod(v.period || EMPTY_PERIOD);
  };
  const sorted = useMemo(() => sortData(filtered, {
    contractor_name: r => r.contractor_name || '',
    vendor_name: r => r.vendor_name || '',
    invoice_number: r => r.invoice_number || '',
    period_start: r => r.period_start || '',
    amount_to_pay: r => r.amount_to_pay || 0,
    ar_invoice_number: r => r.ar_invoice_number || '',
    ar_verified_paid: r => r.ar_verified_paid || 0,
  }), [filtered, sortKey, sortDir]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  async function recordPayment() {
    if (!formData.payment_date || !formData.cash_amount) {
      setPayError('Payment date and cash amount are required');
      return;
    }
    setSaving(true);
    setPayError('');
    try {
      const r = await api.post(`/ap-invoices/${payForm.ap_invoice_id}/payments`, {
        ...formData,
        cash_amount: Number(formData.cash_amount),
        epd_rate: formData.epd_rate ? Number(formData.epd_rate) : null,
        epd_amount: formData.epd_amount ? Number(formData.epd_amount) : 0,
        bgc_amount: formData.bgc_amount ? Number(formData.bgc_amount) : 0,
        advance_deduction_amount: formData.advance_deduction_amount ? Number(formData.advance_deduction_amount) : 0,
      });
      // If this payment includes a BGV deduction pulled from the pending
      // worklist, mark it applied now that a real payment actually exists
      // for it - not at prefill time, since the user may have blanked the
      // BGC field out before saving.
      if (payForm.pending_bgv_id && Number(formData.bgc_amount) > 0) {
        try {
          await api.post(`/hex-payment/bgv-deductions/${payForm.pending_bgv_id}/apply`, { ap_payment_id: r.data.id });
        } catch (e) {
          // Non-fatal - the payment itself already saved successfully;
          // the BGV worklist entry can still be applied manually later.
        }
      }
      setPayForm(null);
      setFormData(EMPTY_FORM);
      load();
    } catch (e) {
      setPayError(e.response?.data?.error || 'Payment failed');
    } finally {
      setSaving(false);
    }
  }

  async function generateAdvice(apInvoiceId, invoiceNumber) {
    setGeneratingAdvice(apInvoiceId);
    try {
      const response = await api.post('/payment-advice/batch', { invoiceIds: [apInvoiceId] }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment-advice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // responseType 'blob' hides the server's JSON error - read it back out
      let msg = err.message || 'Unknown error';
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch {}
      toast('Failed to generate Payment Advice: ' + msg, 'error');
    } finally {
      setGeneratingAdvice(null);
    }
  }

  // Re-checks every AP invoice against its matching AR and brings in any
  // whose AR is already paid but whose readiness was never updated (e.g.
  // PO/period fixed after the AR payment, or changed outside the app).
  // Creating/editing an AP invoice already does this for that one invoice;
  // this button is the catch-all. (2026-09-29)
  async function refreshQueue() {
    setRefreshing(true);
    try {
      const r = await api.post('/ap-invoices/payments-queue/refresh');
      setUploadResult({ message: r.data.message, errors: [] });
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'Refresh failed', 'error');
    } finally {
      setRefreshing(false);
    }
  }

  async function downloadQueueCsv() {
    setDownloadingCsv(true);
    try {
      // Export exactly what's on screen when any filter is narrowing the list.
      const params = filtersActive ? { ids: filtered.map(r => r.ap_invoice_id).join(',') } : undefined;
      const response = await api.get('/ap-invoices/payments-queue/export-csv', { responseType: 'blob', params });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ap-payments-queue-${todayStr}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast('Failed to download CSV', 'error');
    } finally {
      setDownloadingCsv(false);
    }
  }

  async function handleUploadCsv(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCsv(true);
    setUploadResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/ap-invoices/payments-queue/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(r.data);
      load();
    } catch (err) {
      setUploadResult({ message: err.response?.data?.error || 'Upload failed', errors: [] });
    } finally {
      setUploadingCsv(false);
      e.target.value = '';
    }
  }

  // Ticked rows still in the queue (a row paid since is dropped).
  const selectedIds = (queue || []).filter(r => selected.has(r.ap_invoice_id)).map(r => r.ap_invoice_id);
  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.ap_invoice_id));
  function toggleRow(id) {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleAllFiltered() {
    setSelected(s => {
      const n = new Set(s);
      filtered.forEach(r => (allFilteredSelected ? n.delete(r.ap_invoice_id) : n.add(r.ap_invoice_id)));
      return n;
    });
  }
  function openPnc() {
    setPncSource(selectedIds.length ? 'selected' : 'csv');
    setPncPreview(null); setPncError(''); setPncOpen(true);
  }
  async function previewPncSelected() {
    setPncBusy(true); setPncError(''); setPncPreview(null);
    try {
      const r = await api.post('/ap-invoices/payments-queue/pnc-upload/preview', { ids: selectedIds, date: pncDate });
      setPncPreview(r.data);
    } catch (err) {
      setPncError(err.response?.data?.error || 'Preview failed');
    } finally { setPncBusy(false); }
  }
  async function previewPncCsv(e) {
    const file = e.target.files[0];
    if (!file) return;
    setPncBusy(true); setPncError(''); setPncPreview(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/ap-invoices/payments-queue/pnc-upload/preview-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPncPreview(r.data);
    } catch (err) {
      setPncError(err.response?.data?.error || 'Preview failed');
    } finally { setPncBusy(false); e.target.value = ''; }
  }
  async function downloadPnc() {
    setPncBusy(true); setPncError('');
    try {
      const response = await api.post('/ap-invoices/payments-queue/pnc-upload/export',
        { items: pncPreview.items, expect: { count: pncPreview.count, total: pncPreview.total } }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pnc-upload-${todayStr}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast(`PNC upload file downloaded: ${pncPreview.payment_count} payment(s) for ${pncPreview.count} invoice(s), $${money(pncPreview.total)}`, 'success');
    } catch (err) {
      let msg = err.message || 'Export failed';
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch {}
      setPncError(msg);
    } finally { setPncBusy(false); }
  }

  // Header totals follow the filters, so narrowing to one vendor shows
  // what that vendor is owed. Main figure is AFTER the early-payment
  // discount (cash actually going out), with the before-EPD figure under
  // it - the same pair, rounded the same way, as the dashboard's
  // "AP Ready to Pay" card. (2026-09-30)
  const round2 = n => Math.round(n * 100) / 100;
  const totalOutstanding = round2(filtered.reduce((sum, r) => sum + (r.corrected_outstanding ?? (r.amount_to_pay - r.ap_paid)), 0));
  const totalAfterEpd = round2(filtered.reduce((sum, r) => sum + (r.corrected_to_pay_net ?? r.corrected_outstanding ?? (r.amount_to_pay - r.ap_paid)), 0));
  const money = n => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Two tabs (2026-10-22): the queue (invoices still to pay) and payments
  // already made, with their payment-advice status.
  const tab = get('tab', 'queue');
  const tabBar = (
    <div className="filter-bar" style={{ marginBottom: 12 }}>
      <button className={'btn btn-sm ' + (tab === 'queue' ? 'btn-primary' : 'btn-secondary')} onClick={() => set({ tab: '' })}>Queue (to pay)</button>
      <button className={'btn btn-sm ' + (tab === 'made' ? 'btn-primary' : 'btn-secondary')} onClick={() => set({ tab: 'made' })}>Payments made</button>
    </div>
  );
  if (tab === 'made') {
    return (
      <div className="page">
        <div className="page-header">
          <div>
            <h1 className="page-title">AP Payments</h1>
            <p className="page-subtitle">Payments made, with their payment advice status. Send advice from Vendor Remittances.</p>
          </div>
        </div>
        {tabBar}
        <PaymentsMadeTab />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">AP Payments Queue</h1>
        </div>
        {queue && (
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Ready to Pay (after EPD)</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>
                ${money(totalAfterEpd)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Before EPD: ${money(totalOutstanding)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Invoices</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                {filtersActive ? `${filtered.length} of ${queue.length}` : queue.length}
              </div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={refreshQueue} disabled={refreshing}
              title="Re-check all AP invoices against their AR payments and bring in any that are now payable">
              {refreshing ? 'Refreshing…' : '⟳ Refresh'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={downloadQueueCsv} disabled={downloadingCsv}>
              {downloadingCsv ? 'Downloading…' : '↓ Download CSV'}
            </button>
            <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', marginBottom: 0 }}>
              {uploadingCsv ? 'Uploading…' : '↑ Upload CSV'}
              <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleUploadCsv} disabled={uploadingCsv} />
            </label>
            <button className="btn btn-secondary btn-sm" onClick={openPnc}
              title="Build the PNC PINACLE upload file (template &quot;Payment - Contractors 2026&quot;, one payment per vendor) from the ticked rows or an edited queue CSV">
              ⇪ Export PNC upload{selectedIds.length ? ` (${selectedIds.length})` : ''}
            </button>
          </div>
        )}
      </div>

      {tabBar}

      {uploadResult && (
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 16, fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>{uploadResult.message}</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setUploadResult(null)}>×</button>
          </div>
          {uploadResult.errors && uploadResult.errors.length > 0 && (
            <ul style={{ marginTop: 8, paddingLeft: 18, color: 'var(--warning)' }}>
              {uploadResult.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Pay Form Modal */}
      {payForm && (
        <>
          <div className="panel-overlay" onClick={() => { setPayForm(null); setFormData(EMPTY_FORM); setPayError(''); }} />
          <div className="quick-panel" style={{ width: 440 }}>
            <div className="drawer-header">
              <h3 className="drawer-title">Record Payment</h3>
              <button className="drawer-close" onClick={() => { setPayForm(null); setFormData(EMPTY_FORM); setPayError(''); }}>×</button>
            </div>
            <div className="panel-body">
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 16, fontSize: 13 }}>
                <div style={{ fontWeight: 700 }}>{payForm.invoice_number}</div>
                <div style={{ color: 'var(--muted)', fontSize: 12 }}>{payForm.vendor_name} · {payForm.contractor_name}</div>
                <div style={{ marginTop: 6, fontWeight: 600, color: 'var(--accent)' }}>
                  Amount to Pay: ${payForm.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>

              {(formData.epd_rate || formData.bgc_amount) && (
                <div style={{ background: 'var(--info-bg, #1e3a5f22)', color: 'var(--info-text, #60a5fa)', padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: 12, marginBottom: 12 }}>
                  {formData.epd_rate && <div>EPD auto-filled at {formData.epd_rate}% from the linked AR payment.</div>}
                  {formData.bgc_amount && <div>BGC auto-filled from a pending BGV deduction for this contractor.</div>}
                  Adjust or clear either field below before saving if not applicable here.
                </div>
              )}

              {payError && <div style={{ color: 'var(--error)', fontSize: 12, marginBottom: 12 }}>{payError}</div>}

              <div style={{ display: 'grid', gap: 10 }}>
                <div className="form-field">
                  <label>Payment Date *</label>
                  <input type="date" value={formData.payment_date} onChange={e => setFormData(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
                <div className="form-field">
                  <label>Cash Amount *</label>
                  <input type="number" step="0.01" value={formData.cash_amount}
                    placeholder={payForm.outstanding.toFixed(2)}
                    onChange={e => setFormData(f => ({ ...f, cash_amount: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="form-field">
                    <label>EPD Rate (%)</label>
                    <input type="number" step="0.01" value={formData.epd_rate} onChange={e => setFormData(f => ({ ...f, epd_rate: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label>EPD Amount</label>
                    <input type="number" step="0.01" value={formData.epd_amount} onChange={e => setFormData(f => ({ ...f, epd_amount: e.target.value }))} />
                  </div>
                </div>
                <div className="form-field">
                  <label>BGC Amount</label>
                  <input type="number" step="0.01" value={formData.bgc_amount} onChange={e => setFormData(f => ({ ...f, bgc_amount: e.target.value }))} />
                </div>
                <div className="form-field">
                  <label>Advance Deduction</label>
                  <input type="number" step="0.01" value={formData.advance_deduction_amount} onChange={e => setFormData(f => ({ ...f, advance_deduction_amount: e.target.value }))} />
                </div>
                <div className="form-field">
                  <label>Bank Trace ID</label>
                  <input value={formData.trace_id} onChange={e => setFormData(f => ({ ...f, trace_id: e.target.value }))} placeholder="e.g. 202607006967538" />
                </div>
                <div className="form-field">
                  <label>Notes</label>
                  <input value={formData.notes} onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setPayForm(null); setFormData(EMPTY_FORM); setPayError(''); }}>Cancel</button>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={recordPayment} disabled={saving}>
                  {saving ? 'Recording…' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* PNC PINACLE upload panel */}
      {pncOpen && (
        <>
          <div className="panel-overlay" onClick={() => setPncOpen(false)} />
          <div className="quick-panel" style={{ width: 760, maxWidth: '100vw' }}>
            <div className="drawer-header">
              <h3 className="drawer-title">PNC upload file</h3>
              <button className="drawer-close" onClick={() => setPncOpen(false)}>×</button>
            </div>
            <div className="panel-body" style={{ fontSize: 13 }}>
              <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 12 }}>
                One PNC payment per vendor (or direct-paid contractor) and payment date, adding up its invoices; when it settles,
                record that one PNC trace ID on each of its invoices. Upload in PINACLE: Payments &amp; Transfers → Payments → Upload Payment(s), template
                "Payment - Contractors 2026". Uploads land in Pending Approval - check each payee's name, amount and account last 4
                against PNC's saved payee before approving. The file is not kept on the server.
              </div>
              <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                <label><input type="radio" checked={pncSource === 'selected'} onChange={() => { setPncSource('selected'); setPncPreview(null); }} /> Ticked rows ({selectedIds.length})</label>
                <label><input type="radio" checked={pncSource === 'csv'} onChange={() => { setPncSource('csv'); setPncPreview(null); }} /> Edited queue CSV (to_pay_net + date)</label>
              </div>
              {pncSource === 'selected' ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 12 }}>
                  <div className="form-field" style={{ marginBottom: 0 }}>
                    <label>Payment date</label>
                    <input type="date" value={pncDate} onChange={e => { setPncDate(e.target.value); setPncPreview(null); }} />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={previewPncSelected} disabled={pncBusy || !selectedIds.length}>
                    {pncBusy ? 'Checking…' : 'Preview'}
                  </button>
                  {!selectedIds.length && <span style={{ color: 'var(--muted)', fontSize: 12 }}>Tick rows in the queue first.</span>}
                </div>
              ) : (
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', marginBottom: 12 }}>
                  {pncBusy ? 'Checking…' : 'Choose edited queue CSV…'}
                  <input type="file" accept=".csv" style={{ display: 'none' }} onChange={previewPncCsv} disabled={pncBusy} />
                </label>
              )}

              {pncError && <div style={{ color: 'var(--error)', fontSize: 12, marginBottom: 12 }}>{pncError}</div>}

              {pncPreview && (
                <>
                  <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 12 }}>
                    <b>{pncPreview.payment_count}</b> PNC payment(s) for <b>{pncPreview.count}</b> invoice(s), total <b>${money(pncPreview.total)}</b>
                    {pncPreview.rejected.length > 0 && <span style={{ color: 'var(--warning)' }}> · {pncPreview.rejected.length} refused</span>}
                    {pncPreview.skipped.length > 0 && <span style={{ color: 'var(--muted)' }}> · {pncPreview.skipped.length} skipped ($0)</span>}
                  </div>
                  {pncPreview.payments.length > 0 && (
                    <table className="data-table" style={{ marginBottom: 12, fontSize: 12 }}>
                      <thead><tr><th>#</th><th>Payee</th><th>Type</th><th>Account</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th>Note to payee</th></tr></thead>
                      <tbody>
                        {pncPreview.payments.map(p => (
                          <tr key={p.payment_no}>
                            <td>{p.payment_no}</td><td>{p.payee}</td><td>{p.payee_type === 'I' ? 'Individual' : 'Business'}</td>
                            <td>{p.account_type} ••{p.account_last4}</td><td>{p.date}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>${money(p.amount)}</td><td>{p.note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {pncPreview.rows.length > 0 && (
                    <details style={{ marginBottom: 12 }}>
                      <summary style={{ cursor: 'pointer', fontSize: 12 }}>Invoices in each payment ({pncPreview.rows.length})</summary>
                      <table className="data-table" style={{ marginTop: 8, fontSize: 12 }}>
                        <thead><tr><th>#</th><th>AP invoice</th><th>Contractor</th><th>Period</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                        <tbody>
                          {[...pncPreview.rows].sort((a, b) => a.payment_no - b.payment_no).map(r => (
                            <tr key={r.ap_invoice_id}>
                              <td>{r.payment_no}</td><td>{r.invoice_number}</td><td>{r.contractor}</td><td>{r.period}</td>
                              <td style={{ textAlign: 'right' }}>${money(r.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  )}
                  {pncPreview.rejected.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontWeight: 600, color: 'var(--warning)' }}>Refused - not in the file</div>
                      <ul style={{ paddingLeft: 18, color: 'var(--warning)', fontSize: 12 }}>
                        {pncPreview.rejected.map((r, i) => <li key={i}>{r.invoice_number || `AP ${r.ap_invoice_id}`}{r.payee ? ` (${r.payee})` : ''}: {r.reason}</li>)}
                      </ul>
                    </div>
                  )}
                  <button className="btn btn-primary" onClick={downloadPnc} disabled={pncBusy || pncPreview.count === 0}>
                    {pncBusy ? 'Working…' : `↓ Download PNC file (${pncPreview.payment_count} payment${pncPreview.payment_count === 1 ? '' : 's'})`}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Filter bar */}
      <div className="filter-bar">
        <PeriodFilter value={period} onChange={setPeriod} />
        <input type="text" className="filter-search"
          placeholder="Search contractor, vendor, invoice #…"
          value={search} onChange={e => set({ search: e.target.value, page: 1 })} />
        <MultiSelectFilter label="Vendor" options={vendorOptions.map(o => ({ value: o.id, label: o.name }))} selected={vendorFilter} onChange={v => set({ vendor: listParam(v), page: 1 })} />
        <MultiSelectFilter label="Contractor" options={contractorOptions.map(o => ({ value: o.id, label: o.name }))} selected={contractorFilter} onChange={v => set({ contractor: listParam(v), page: 1 })} />
        <MultiSelectFilter label="Status" options={Object.entries(STATUS_FILTERS).map(([value, f]) => ({ value, label: f.label }))} selected={statusFilter} onChange={v => set({ status: listParam(v), page: 1 })} />
        <MultiSelectFilter label="Invoice Type" options={Object.entries(INVOICE_TYPES).map(([value, f]) => ({ value, label: f.label, count: (queue || []).filter(f.test).length }))} selected={typeFilter} onChange={v => set({ type: listParam(v), page: 1 })} />
        <SavedViews pageKey="ap_payments_queue" currentState={savedViewState} canSave={chips.length > 0} onApply={applySavedView} />
      </div>
      <FilterChips chips={chips} onClearAll={clearAllFilters} />

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && queue === null && <StateBlock type="loading" />}
      {!error && queue !== null && filtered.length === 0 && (
        <StateBlock type="noResults" message={queue.length === 0 ? "No AP invoices ready to pay. AR payments must be verified first." : undefined} />
      )}

      {!error && filtered.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}>
                  <input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered}
                    title="Tick / untick every row matching the filters (for the PNC upload file)" />
                </th>
                <SortTh label="Contractor" sortKey="contractor_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Vendor" sortKey="vendor_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="AP Invoice #" sortKey="invoice_number" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Period" sortKey="period_start" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="AP Amount" sortKey="amount_to_pay" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                <SortTh label="AR Invoice" sortKey="ar_invoice_number" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="AR Received" sortKey="ar_verified_paid" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(row => {
                const isPartial = row.ar_verified_paid < row.ar_amount - 0.01;
                const outstanding = row.corrected_outstanding ?? (row.amount_to_pay - row.ap_paid);
                return (
                  <tr key={row.ap_invoice_id}>
                    <td><input type="checkbox" checked={selected.has(row.ap_invoice_id)} onChange={() => toggleRow(row.ap_invoice_id)} /></td>
                    <td><EntityLink type="contractor" id={row.contractor_id}>{row.contractor_name}</EntityLink></td>
                    <td><EntityLink type="vendor" id={row.vendor_id}>{row.vendor_name || "—"}</EntityLink></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <EntityLink type="ap_invoice" id={row.ap_invoice_id} style={{ fontWeight: 600 }}>{row.invoice_number}</EntityLink>
                        {(row.invoice_type || 'standard') !== 'standard' && (
                          <span title="Allowance / Reimbursement - paid up to what Hexaware paid on the AR"
                            style={{ fontSize: 10, background: '#8b5cf622', color: '#8b5cf6', border: '1px solid #8b5cf644',
                              borderRadius: 4, padding: '1px 5px', cursor: 'help' }}>
                            Expense
                          </span>
                        )}
                        {isPartial && (
                          <span title={`AR partially paid: $${row.ar_verified_paid.toLocaleString()} of $${row.ar_amount.toLocaleString()}`}
                            style={{ fontSize: 10, background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b44',
                              borderRadius: 4, padding: '1px 5px', cursor: 'help' }}>
                            ⚡ Partial AR
                          </span>
                        )}
                        {row.queue_note && (
                          <span title={row.queue_note}
                            style={{ fontSize: 10, background: '#0ea5e922', color: '#0284c7', border: '1px solid #0ea5e944',
                              borderRadius: 4, padding: '1px 5px', cursor: 'help' }}>
                            {row.queue_note}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {row.period_start && row.period_end
                        ? `${row.period_start} → ${row.period_end}`
                        : row.period_start || '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      ${outstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ fontSize: 12 }}><EntityLink type="ar_invoice" id={row.ar_invoice_id}>{row.ar_invoice_number || '—'}</EntityLink></td>
                    <td style={{ textAlign: 'right', fontSize: 12 }}>
                      ${(row.ar_verified_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-primary"
                          onClick={() => {
                            setPayForm({ ...row, outstanding });
                            // EPD rate comes from the linked AR invoice's own
                            // verified payment, applied to THIS AP invoice's
                            // outstanding amount (a different dollar figure
                            // than the AR side). BGC comes from the pending
                            // BGV worklist for this contractor, if any -
                            // marked applied only once the payment actually
                            // saves (see recordPayment), never at prefill.
                            const epdRatePct = row.ar_epd_rate ? (row.ar_epd_rate * 100).toString() : '';
                            const epdAmt = row.corrected_epd_amount ?? (row.ar_epd_rate ? Math.round(outstanding * row.ar_epd_rate * 100) / 100 : 0);
                            const bgcAmt = row.pending_bgv_amount || 0;
                            const netCash = Math.round((outstanding - epdAmt - bgcAmt) * 100) / 100;
                            setFormData({
                              ...EMPTY_FORM,
                              cash_amount: netCash.toFixed(2),
                              epd_rate: epdRatePct,
                              epd_amount: epdAmt ? epdAmt.toFixed(2) : '',
                              bgc_amount: bgcAmt ? bgcAmt.toFixed(2) : '',
                            });
                          }}>
                          Pay
                        </button>
                        <button className="btn btn-sm btn-secondary"
                          disabled={generatingAdvice === row.ap_invoice_id}
                          onClick={() => generateAdvice(row.ap_invoice_id, row.invoice_number)}>
                          {generatingAdvice === row.ap_invoice_id ? '…' : 'PDF'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={page} pageSize={pageSize} total={filtered.length}
            onPageChange={p => set({ page: p })}
            onPageSizeChange={s => set({ pageSize: s, page: 1 })} />
        </>
      )}
    </div>
  );
}
